import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const IMPERSONATION_STORAGE_KEY = 'admin_impersonation';
const IMPERSONATION_MAX_DURATION_MS = 30 * 60 * 1000; // 30 minutes

interface ImpersonationState {
  impersonatedUserId: string | null;
  impersonatedUserEmail: string | null;
  startedAt: number | null; // Unix timestamp in ms
  reason: string | null;
}

interface ImpersonationContextValue {
  impersonatedUserId: string | null;
  impersonatedUserEmail: string | null;
  isImpersonating: boolean;
  startedAt: number | null;
  reason: string | null;
  remainingTimeMs: number;
  startImpersonation: (userId: string, email: string, reason?: string) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { hasRole, user } = useAuth();
  const [state, setState] = useState<ImpersonationState>({
    impersonatedUserId: null,
    impersonatedUserEmail: null,
    startedAt: null,
    reason: null,
  });
  const [remainingTimeMs, setRemainingTimeMs] = useState(0);

  // Calculate remaining time and auto-expire
  useEffect(() => {
    if (!state.startedAt) {
      setRemainingTimeMs(0);
      return;
    }

    const updateRemainingTime = () => {
      const elapsed = Date.now() - state.startedAt!;
      const remaining = Math.max(0, IMPERSONATION_MAX_DURATION_MS - elapsed);
      setRemainingTimeMs(remaining);

      // Auto-expire when time runs out
      if (remaining <= 0) {
        logger.warn('Impersonation session expired due to time limit', {
          userId: state.impersonatedUserId,
          email: state.impersonatedUserEmail,
          duration: IMPERSONATION_MAX_DURATION_MS,
        });
        toast.warning('Impersonation session expired (30 minute limit reached)');
        stopImpersonation();
      }
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.startedAt]);

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ImpersonationState;

        // Check if session has expired
        if (parsed.startedAt) {
          const elapsed = Date.now() - parsed.startedAt;
          if (elapsed >= IMPERSONATION_MAX_DURATION_MS) {
            logger.info('Impersonation session expired on load', { userId: parsed.impersonatedUserId });
            localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
            return;
          }
        }

        // Only restore if admin role is still valid (security check)
        if (hasRole('admin')) {
          setState(parsed);
          logger.info('Impersonation state restored', {
            userId: parsed.impersonatedUserId,
            email: parsed.impersonatedUserEmail,
            reason: parsed.reason,
          });
        } else {
          // Clear if user is no longer admin
          localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        }
      }
    } catch (error) {
      logger.error('Error loading impersonation state', { error });
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, [hasRole]);

  // Log impersonation to audit log
  const logToAudit = useCallback(async (action: 'impersonation_started' | 'impersonation_ended', targetUserId: string | null, details: Record<string, unknown>) => {
    try {
      await supabase.from('admin_activity_log').insert({
        action,
        actor_id: user?.id,
        target_id: targetUserId,
        target_type: 'user',
        details,
      });
    } catch (error) {
      logger.error('Failed to log impersonation to audit', { error, action });
    }
  }, [user?.id]);

  const startImpersonation = useCallback((userId: string, email: string, reason?: string) => {
    // Security check: only admins can impersonate
    if (!hasRole('admin')) {
      logger.error('Non-admin attempted to impersonate', { userId, email });
      throw new Error('Only admins can impersonate users');
    }

    const newState: ImpersonationState = {
      impersonatedUserId: userId,
      impersonatedUserEmail: email,
      startedAt: Date.now(),
      reason: reason || 'No reason provided',
    };

    setState(newState);
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(newState));

    logger.info('Impersonation started', { userId, email, reason: newState.reason });

    // Log to audit
    logToAudit('impersonation_started', userId, {
      targetEmail: email,
      reason: newState.reason,
      maxDurationMinutes: 30,
    });

    toast.info(`Now viewing as ${email}. Session expires in 30 minutes.`);
  }, [hasRole, logToAudit]);

  const stopImpersonation = useCallback(() => {
    const duration = state.startedAt ? Date.now() - state.startedAt : 0;

    logger.info('Impersonation stopped', {
      userId: state.impersonatedUserId,
      email: state.impersonatedUserEmail,
      durationMs: duration,
      reason: state.reason,
    });

    // Log to audit
    if (state.impersonatedUserId) {
      logToAudit('impersonation_ended', state.impersonatedUserId, {
        targetEmail: state.impersonatedUserEmail,
        durationMs: duration,
        reason: state.reason,
      });
    }

    setState({
      impersonatedUserId: null,
      impersonatedUserEmail: null,
      startedAt: null,
      reason: null,
    });
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  }, [state.impersonatedUserId, state.impersonatedUserEmail, state.startedAt, state.reason, logToAudit]);

  const value: ImpersonationContextValue = {
    impersonatedUserId: state.impersonatedUserId,
    impersonatedUserEmail: state.impersonatedUserEmail,
    isImpersonating: state.impersonatedUserId !== null,
    startedAt: state.startedAt,
    reason: state.reason,
    remainingTimeMs,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonationContext() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonationContext must be used within an ImpersonationProvider');
  }
  return context;
}
